import jsPDF from 'jspdf';
import { getMediaForMonth } from '@/lib/gallery';
import { Firestore, doc, getDoc } from 'firebase/firestore';
import { BRAND_COLOR } from './pdf-constants';

// --- Image Utilities ---

/**
 * Loads an image from a URL into an HTMLImageElement using fetch.
 * This handles CORS better by relying on fetch's behavior and Blob Object URLs.
 */
export async function loadImage(url: string): Promise<HTMLImageElement> {
    try {
        const res = await fetch(url, { cache: 'no-cache' }); // Force fresh request to avoid opaque cache
        if (!res.ok) throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(blob);
            img.src = objectUrl;
            img.onload = () => {
                // Clean up memory after a short delay or let GC handle
                // We need it for the immediate canvas op
                setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                resolve(img);
            };
            img.onerror = (e) => reject(new Error(`Failed to parse image blob`));
        });
    } catch (e) {
        throw new Error(`Failed to load image ${url}: ${(e as Error).message}`);
    }
}

/**
 * Prepares an image for the PDF by resizing and cropping it to match the target dimensions.
 * Uses an offscreen canvas to perform the transformation, effectively 'rasterizing' the crop.
 * This prevents PDF rendering issues like black lines caused by complex clipping paths on large images.
 * 
 * @param img The source HTMLImageElement
 * @param targetW Inches width in PDF
 * @param targetH Inches height in PDF
 * @param dpi Output resolution (default 300 for print)
 * @returns Data URL of the processed JPEG
 */
export function processImageForPdf(
    img: HTMLImageElement,
    targetW: number,
    targetH: number,
    dpi: number = 300
): string {
    // 1. Calculate Target Pixels
    const canvasW = Math.ceil(targetW * dpi);
    const canvasH = Math.ceil(targetH * dpi);

    // 2. Setup Canvas
    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('Could not get canvas context');

    // 3. Calculate "Cover" Fit
    // We want to scale the image so it covers canvasW x canvasH
    // Then center it.
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const targetAspect = canvasW / canvasH;

    let renderW, renderH, renderX, renderY;

    if (imgAspect > targetAspect) {
        // Image is Wider than target: Match Height, Crop Width
        renderH = canvasH;
        renderW = canvasH * imgAspect;
        renderX = (canvasW - renderW) / 2; // Center horizontally
        renderY = 0;
    } else {
        // Image is Taller/Narrower than target: Match Width, Crop Height
        renderW = canvasW;
        renderH = canvasW / imgAspect;
        renderX = 0;
        renderY = (canvasH - renderH) / 2; // Center vertically
    }

    // 4. White Background (handling transparency if any, though usually JPEG)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvasW, canvasH);

    // 5. Draw (Resize & Crop)
    // The browser handles the high-quality downsampling usually better than raw PDF linear interpolation
    ctx.drawImage(img, renderX, renderY, renderW, renderH);

    // 6. Output as JPEG to save space vs PNG
    return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Main helper to draw an image cover into the PDF.
 * Pipeline: Load -> Process (Canvas) -> Add to PDF.
 */
export async function drawImageCover(
    doc: jsPDF,
    imageUrl: string,
    x: number,
    y: number,
    w: number,
    h: number,
    unit: 'mm' | 'in' = 'in'
) {
    try {
        // Safety check for dimensions
        if (!w || !h || w <= 0 || h <= 0) {
            console.warn("Invalid dimensions for drawImageCover", { w, h });
            return;
        }

        // 1. Load
        const img = await loadImage(imageUrl);

        // 2. Process (Rotate, Resize, Crop to strict dimensions)
        // We use a reasonably high DPI (150-200) for screen/print balance.
        // Convert to inches for DPI calculation if needed
        let wIn = w;
        let hIn = h;
        if (unit === 'mm') {
            wIn = w / 25.4;
            hIn = h / 25.4;
        }

        const processedDataUrl = processImageForPdf(img, wIn, hIn, 200);

        // 3. Draw
        // Note: We don't need clipping paths anymore because the canvas output IS the exact size!
        doc.addImage(processedDataUrl, 'JPEG', x, y, w, h);

    } catch (e) {
        console.error("Failed to draw image cover", e);
        // Fallback: Solid Brand Color Box with Error Text
        doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.rect(x, y, w, h, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        // doc.text("Image Error", x + w/2, y + h/2, { align: 'center' });
    }
}

/**
 * Draws an image scaled to FIT within the target box (Contain), maintaining aspect ratio.
 * No cropping occurs. The image is centered in the box.
 */
export async function drawImageContain(
    doc: jsPDF,
    imageUrl: string,
    x: number,
    y: number,
    w: number,
    h: number
) {
    try {
        const img = await loadImage(imageUrl);
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const targetAspect = w / h;

        let drawW, drawH;

        if (imgAspect > targetAspect) {
            // Image is wider than box (relative to aspect) -> Fit Width
            drawW = w;
            drawH = w / imgAspect;
        } else {
            // Image is taller -> Fit Height
            drawH = h;
            drawW = h * imgAspect;
        }

        // Center
        const offX = (w - drawW) / 2;
        const offY = (h - drawH) / 2;

        // We can use processImageForPdf if we want to resample, but for logos 
        // (often vector or sharp lines), direct addImage is often safer unless huge.
        // However, to reuse the "Safe Fetch" logic, we already have `img` loaded.
        // processImageForPdf creates a JPEG. Logos might need transparency (PNG).
        // IF the logo is PNG, processImageForPdf (which fills white bg) might be bad if we wanted transparency.
        // BUT we are drawing on White paper, so white bg is fine.
        // Let's use direct addImage from the Image Element to preserve PNG transparency if present.
        doc.addImage(img, 'PNG', x + offX, y + offY, drawW, drawH);

    } catch (e) {
        console.warn("Failed to draw logo contain", e);
    }
}

// Deterministic fetch (first available)
export async function fetchMonthImages(firestore: Firestore, monthKeys: string[]) {
    const mediaMap: Record<string, string> = {};
    const usedIds: string[] = [];

    for (const k of monthKeys) {
        try {
            const items = await getMediaForMonth(firestore, k, 1, usedIds);
            if (items.length > 0) {
                if (items[0].id) usedIds.push(items[0].id);
                if (items[0].url) mediaMap[k] = items[0].url;
            }
        } catch (e) {
            console.error(`Error fetching media for ${k}`, e);
        }
    }
    return mediaMap;
}

// Randomized unique fetch (picks 1 from pool of candidates)
export async function fetchUniqueRandomImages(firestore: Firestore, monthKeys: string[]) {
    const mediaMap: Record<string, string> = {};
    const usedIds: string[] = [];

    for (const k of monthKeys) {
        try {
            // Fetch pool of 10 candidates to pick from
            const items = await getMediaForMonth(firestore, k, 10, usedIds);

            if (items.length > 0) {
                // Pick Random
                const randomIndex = Math.floor(Math.random() * items.length);
                const selected = items[randomIndex];

                if (selected.id) usedIds.push(selected.id);
                if (selected.url) mediaMap[k] = selected.url;
            }
        } catch (e) {
            console.error(`Error fetching unique media for ${k}`, e);
        }
    }
    return mediaMap;
}

export async function fetchClubInfo(firestore: Firestore) {
    const info = {
        clubName: 'Snohomish Radio Aero Club',
        clubLogoUrl: '',
        clubWebsite: 'https://snohomishradioaeroclub.org',
        clubEmail: '',
        fieldAddress: ''
    };

    try {
        const fieldsToFetch = ['clubName', 'clubLogoUrl', 'clubWebsite', 'clubEmail', 'fieldAddress'];
        const results = await Promise.all(
            fieldsToFetch.map(async (field) => {
                try {
                    const d = await getDoc(doc(firestore, 'membershipInfo', field));
                    if (d.exists()) {
                        return { key: field, val: d.data().value };
                    }
                } catch (e) {
                    // ignore missing
                }
                return null;
            })
        );

        results.forEach(r => {
            if (r && r.val) {
                // @ts-ignore
                info[r.key] = r.val;
            }
        });

    } catch (e) {
        console.warn("Failed to fetch club info", e);
    }
    return info;
}
